/**
 * carriers/nimitz — Nimitz carrier (CVN-09)
 * @specialization 기함대 사령관 · 읽기 전용 전략 지휘·판단 — 아키텍처 결정 · 심층 기술 분석 · 트레이드오프 재결
 *
 * Nimitz carrier를 프레임워크에 등록합니다.
 */

import type { CarrierMetadata, CarrierPersonaDefaults } from "../dispatch/types.js";

import { CARRIER_JOBS_SELF_CALL_HINT } from "../constants.js";

export const NIMITZ_DEFAULTS: CarrierPersonaDefaults = {
  id: "nimitz",
  displayName: "Nimitz",
  slot: 1,
  agent: {
    dispatch: {
      defaultCliType: "claude",
      defaultModel: "opus[1m]",
      defaultEffort: "max",
    },
  },
};

export const CARRIER_METADATA: CarrierMetadata = {
  // ── Tier 1: Routing ──
  title: "Strategic Command & Judgment",
  summary: "Read-only strategic command — decides the technical path through doctrinal judgment, architecture decisions, deep analysis, and trade-off adjudication without entering the implementation path.",
  category: "strategy",
  whenToUse: [
    "architecture and design decisions",
    "choosing between competing technical paths before planning or implementation",
    "deadlock breaking (carrier failed 2+ times)",
    "code self-review (read-only)",
    "deep technical analysis and trade-off evaluation",
  ],
  whenNotToUse: [
    "any code modification or file editing (Nimitz is strictly read-only)",
    "PRD/task decomposition, delivery planning, or markdown work-plan generation (→kirov)",
  ],
  requestBlocks: [
    { tag: "context", hint: "Background situation, current state, and relevant history.", required: true },
    { tag: "problem", hint: "The specific question, decision point, or challenge to analyze.", required: true },
    { tag: "constraints", hint: "Hard constraints, deadlines, compatibility requirements.", required: false },
    { tag: "artifacts", hint: "Relevant code snippets, file paths, error logs to examine.", required: false },
  ],
  allowedExecutorTools: ["carrier_jobs", "plan_read"],

  // ── Tier 2: Composition ──
  permissions: [
    "CRITICAL: Strictly read-only. NEVER delegate code modification or file editing to this carrier.",
    "CRITICAL: NEVER dispatch Nimitz without prior reconnaissance — if recon is needed, dispatch vanguard/tempest FIRST. Hard prerequisite, not a suggestion.",
    "Full access to read the codebase and execute read-only commands for analysis.",
    "MUST NOT decompose work into task waves, delivery schedules, or implementation checklists — handoff belongs to Kirov.",
  ],
  outputFormat:
    `Verbosity constraints: bottom line max 3 sentences, action plan max 7 steps (2 sentences each), no preamble, no question restatement, no conversational filler. Prefer compact bullets.\n` +
    `Response structure (3-tier):\n` +
    `[Required] always include:\n` +
    `  **Bottom line** — 2-3 sentences capturing the recommendation.\n` +
    `  **Action plan** — Numbered implementation steps.\n` +
    `  **Effort estimate** — One of: Quick(<1h) / Short(1-4h) / Medium(1-2d) / Large(3d+).\n` +
    `  **Planning constraints** — Fixed decisions, constraints, or guardrails Kirov/Ohio should treat as settled inputs.\n` +
    `[If applicable] include when relevant:\n` +
    `  **Why this approach** — Reasoning and key trade-offs (max 4 bullets).\n` +
    `  **Watch out for** — Risks, edge cases, mitigation strategies (max 3 bullets).\n` +
    `[Edge cases] only when genuinely applicable:\n` +
    `  **Escalation triggers** — Conditions that justify a more complex solution.\n` +
    `  **Alternative sketch** — High-level outline of the backup path only.`,
  principles: [
    CARRIER_JOBS_SELF_CALL_HINT,
    "Delivers exactly ONE best-path recommendation — not a menu of options.",
    "Always favors the simplest viable solution. Complexity only when simplicity provably fails constraints.",
    "Decide the technical path — do not orchestrate execution waves, task matrices, or delivery backlogs.",
    "Return stable planning inputs that Kirov and Ohio can treat as fixed unless explicitly revisited.",
  ],
};
